import { Link } from 'react-router-dom'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ModalShell } from './ModalShell'
import { usePartPriceHistory } from './useDrilldown'

function formatDateShort(iso: string): string {
  const d = new Date(iso)
  return `${d.getDate().toString().padStart(2, '0')}.${(d.getMonth() + 1).toString().padStart(2, '0')}`
}

const CURRENCY_SIGN: Record<string, string> = { USD: '$', EUR: '€', CNY: '¥', RUB: '₽' }
const currencySign = (code: string) => CURRENCY_SIGN[code] ?? code

export function PartPriceHistoryModal({
  target,
  onClose,
}: {
  target: { partNumber: string; brand: string | null }
  onClose: () => void
}) {
  const { data, isLoading, isError } = usePartPriceHistory(target)

  return (
    <ModalShell
      title={target.partNumber}
      subtitle={`${target.brand ?? '—'} · история цены`}
      onClose={onClose}
      size="lg"
    >
      {isLoading && <p className="text-sm text-gray-400">Загрузка…</p>}
      {isError && <p className="text-sm text-red-600">Не удалось загрузить данные</p>}
      {data && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Запросов" value={data.stats.requestsCount} />
            <Stat label="Клиентов" value={data.stats.clientsCount} />
            {/* Валюта — та, в которой квотировали (19.09.2026): раньше цены
                в долларах и юанях подписывались знаком ₽. Нулевые цены
                («Не найдено») в статистику больше не входят. */}
            <Stat
              label="Мин. цена"
              value={
                data.stats.minPrice === null
                  ? '—'
                  : `${currencySign(data.stats.currency)} ${data.stats.minPrice.toFixed(2)}`
              }
            />
            <Stat
              label="Макс. цена"
              value={
                data.stats.maxPrice === null
                  ? '—'
                  : `${currencySign(data.stats.currency)} ${data.stats.maxPrice.toFixed(2)}`
              }
            />
          </div>

          {data.pricePoints.length > 0 && (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={120}>
                <LineChart
                  data={data.pricePoints.map((p) => ({
                    date: formatDateShort(p.date),
                    price: p.price,
                  }))}
                  margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ r: 3, fill: '#3b82f6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Поставщики</div>
              {data.suppliers.length === 0 ? (
                <p className="text-sm text-gray-400">Нет квот</p>
              ) : (
                <div className="space-y-1">
                  {data.suppliers.slice(0, 8).map((s) => (
                    <div key={s.name} className="flex items-center justify-between text-sm">
                      <span className="truncate text-gray-800">{s.name}</span>
                      <span className="text-xs text-gray-500 tabular-nums shrink-0 ml-2">
                        {s.count} · ср. {currencySign(data.stats.currency)} {s.avgPrice.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Клиенты</div>
              {data.clients.length === 0 ? (
                <p className="text-sm text-gray-400">Нет данных</p>
              ) : (
                <div className="space-y-1">
                  {data.clients.slice(0, 8).map((c) => (
                    <Link
                      key={c.id}
                      to={`/clients/${c.id}`}
                      onClick={onClose}
                      className="flex items-center justify-between text-sm hover:bg-gray-50 rounded px-1"
                    >
                      <span className="truncate text-gray-800">{c.name}</span>
                      <span className="text-xs text-gray-500 tabular-nums shrink-0 ml-2">
                        {c.count}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </ModalShell>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-semibold text-gray-900 mt-0.5">{value}</div>
    </div>
  )
}
